import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Call from "@/model/interface/Call";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CallSupervisorSessions } from "@/components/pages/calls/CallSupervisorSessions";
import { CallFlowStepsList } from "@/components/pages/calls/CallFlowStepsList";
import { useLazyGetCallRecordingUrlQuery, useMarkCallRecordingReviewedMutation, useUpdateCallMutation } from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";
import { devicePlatformLabel } from "@/util/device";
import { cn } from "@/lib/utils";
import CallDispositionMenuItems from "./CallDispositionMenuItems";
import CallSummaryBlock from "./CallSummaryBlock";

interface CallExpandedPanelProps {
  call: Call;
  focusNotesRequest?: number;
}

const CallExpandedPanel: React.FC<CallExpandedPanelProps> = ({ call, focusNotesRequest = 0 }) => {
  const [getCallRecordingUrl, { isFetching: isLoadingRecording }] = useLazyGetCallRecordingUrlQuery();
  const [markCallRecordingReviewed] = useMarkCallRecordingReviewedMutation();
  const [updateCall] = useUpdateCallMutation();
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState(call.notes ?? "");
  const notesInputRef = useRef<HTMLInputElement>(null);
  const callId = call.id;
  const hasRecording = !!call.recording;
  const recordingReviewed = !!call.recording?.reviewedAt;
  const notesChanged = notesDraft.trim() !== (call.notes ?? "").trim();

  const saveNotes = () => {
    if (callId) {
      void updateCall({ id: callId, notes: notesDraft.trim() });
    }
  };

  useEffect(() => {
    setNotesDraft(call.notes ?? "");
  }, [call.notes]);

  useEffect(() => {
    if (!callId || !hasRecording || recordingUrl) {
      return;
    }
    let cancelled = false;
    getCallRecordingUrl(callId)
      .unwrap()
      .then((response) => {
        if (cancelled) {
          return;
        }
        setRecordingUrl(response.url);
        if (!recordingReviewed) {
          void markCallRecordingReviewed(callId);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          toast.error(apiErrorMessage(error, "Failed to load the recording"));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [callId, hasRecording, recordingReviewed, recordingUrl, getCallRecordingUrl, markCallRecordingReviewed]);

  useEffect(() => {
    if (focusNotesRequest <= 0) {
      return;
    }
    const frame = requestAnimationFrame(() => {
      notesInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [focusNotesRequest]);

  return (
    <div className="space-y-2" onClick={(event) => event.stopPropagation()}>
      {hasRecording && recordingUrl && <audio controls className="h-10 w-full max-w-md" src={recordingUrl} />}
      {hasRecording && !recordingUrl && isLoadingRecording && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading recording
        </div>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className={cn("h-7 rounded-full text-xs gap-1.5", !call.disposition && "border-dashed text-muted-foreground")}
            title="Change disposition"
          >
            {call.disposition ? (
              <>
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: call.disposition.color }} />
                {call.disposition.name}
              </>
            ) : (
              "No disposition, set one"
            )}
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-64 overflow-y-auto">
          <CallDispositionMenuItems call={call} />
        </DropdownMenuContent>
      </DropdownMenu>
      {call.recording?.summary && callId && <CallSummaryBlock callId={callId} summary={call.recording.summary} />}
      {call.callScript && <p className="text-xs text-muted-foreground">Script: {call.callScript.name}</p>}
      {call.platform && <p className="text-xs text-muted-foreground">Device: {devicePlatformLabel(call.platform)}</p>}
      {call.voicemailMessageName && (
        <p className="text-xs text-muted-foreground">
          Voicemail dropped: {call.voicemailMessageName}
          {call.voicemailMessageSeconds ? ` · ${call.voicemailMessageSeconds} s` : ""}
        </p>
      )}
      <div className="flex items-center gap-2">
        <Input
          ref={notesInputRef}
          value={notesDraft}
          onChange={(event) => setNotesDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && notesChanged) {
              event.preventDefault();
              saveNotes();
            }
          }}
          placeholder="Call notes"
          className="h-8 max-w-md text-sm"
        />
        {notesChanged && (
          <Button size="sm" variant="outline" onClick={saveNotes}>
            Save note
          </Button>
        )}
      </div>
      {callId && (
        <>
          <CallSupervisorSessions callId={callId} />
          {call.callFlow && <CallFlowStepsList callId={callId} />}
        </>
      )}
    </div>
  );
};

export default CallExpandedPanel;
